import { RequestStatus } from 'features/request/requestSlice';
import { useRequest } from 'features/request/useRequest';
import { useResetMultipleStoreData } from 'features/request/useResetStoreData';
import type {
    BimWorkOrderPriceData,
    BimWorkOrderPriceRowData,
    BimWorkOrderTotalItemData,
    BimChapterEnum,
    BimPriceLevelEnum,
} from 'gql/graphql';

import { useEffect, useMemo } from 'react';

import { useBimWorkOrderTotalsRow } from '../../hooks';
import { BimWorkOrderRowDetailsType, BimWorkOrderRowTypeEnum } from '../../types';
import { bimWorkOrderPriceRows } from '../bimWorkOrderPriceQueries';
import type { IBimWorkOrderRowDetails } from '../cellRenderers';

export const useBimWorkOrderPriceDetails = ({
    checkedPriceRowForDetails,
    chapter,
    constructionTypeId,
    workOrderId,
    workOrderObjectId,
    totalChapterDetailsRowOpenOuter,
    regularDetailsFilterValues,
    priceLevel,
}: {
    totalChapterDetailsRowOpenOuter: { rowType: BimWorkOrderRowTypeEnum.TotalsString; data: null } | null;
    checkedPriceRowForDetails: IBimWorkOrderRowDetails<BimWorkOrderTotalItemData | BimWorkOrderPriceData | null> | null;
    workOrderId: number;
    constructionTypeId: number | undefined;
    chapter: BimChapterEnum | undefined;
    workOrderObjectId: number | undefined;
    regularDetailsFilterValues: Record<string, string[]>;
    priceLevel: BimPriceLevelEnum | undefined;
}) => {
    const { rowType, data, detailsType } = checkedPriceRowForDetails || {};

    const detailsVars = useMemo(() => {
        if (data && 'id' in data) {
            return { workOrderPriceId: data?.id, priceLevel };
        }

        return undefined;
    }, [data, priceLevel]);

    const isRequestLegit = Boolean(
        !!checkedPriceRowForDetails &&
            detailsVars?.workOrderPriceId &&
            rowType === BimWorkOrderRowTypeEnum.RegularString &&
            detailsType === BimWorkOrderRowDetailsType.Details,
    );

    /*
     * данные для состава сметы
     * */
    const [detailsData, detailsStatus] = useRequest({
        requestId: 'workOrderPriceRowDetails',
        query: bimWorkOrderPriceRows,
        withEffect: isRequestLegit,
        variables: detailsVars,
    });

    const memoizedDetailsRequestIds = useMemo(
        () => ['workOrderPriceRowDetails', 'bimWorkOrderPriceRowCodeFilter', 'bimWorkOrderPriceRowNameFilter'],
        [],
    );

    const [resetDetailsRows] = useResetMultipleStoreData({
        requestIds: memoizedDetailsRequestIds,
    });

    useEffect(() => {
        if (!checkedPriceRowForDetails) {
            resetDetailsRows();
        }
    }, [checkedPriceRowForDetails, resetDetailsRows]);

    const isRegularDetailsLoading = detailsStatus === RequestStatus.FETCHING;

    const regularDetailsRows = useMemo(() => {
        const mapped =
            detailsData?.bimWorkOrderPriceRows?.items?.reduce(
                (
                    acc: {
                        [key: number]: {
                            value: { name: string | undefined | null; isGroupTitle: boolean };
                            children: BimWorkOrderPriceRowData[];
                        };
                    },
                    row,
                ) => {
                    if (!acc[row.sectionId]) {
                        acc[row.sectionId] = {
                            value: { name: row.sectionNane, isGroupTitle: true },
                            children: [row],
                        };
                    } else {
                        acc[row.sectionId].children.push(row);
                    }

                    return acc;
                },
                {},
            ) ?? {};

        return Object.values(mapped).reduce((acc: any[], row) => {
            acc.push({ planValue: row.value });
            if (row.children.length) {
                row.children.forEach((item) => {
                    let isPassedFilters = true;

                    if (regularDetailsFilterValues.code?.length && item?.value?.code) {
                        isPassedFilters = regularDetailsFilterValues.code.includes(item.value.code);
                    }

                    if (isPassedFilters && regularDetailsFilterValues.name?.length && item?.value?.name) {
                        isPassedFilters = regularDetailsFilterValues.name.includes(item.value.name);
                    }

                    if (isPassedFilters) {
                        acc.push({ ...item, planValue: item.value });
                    }
                });
            }

            return acc;
        }, []);
    }, [detailsData?.bimWorkOrderPriceRows?.items, regularDetailsFilterValues]);

    /*
     * данные для деталей итогов
     * */
    const { rowTotalsData, isLoading: isTotalsLoading } = useBimWorkOrderTotalsRow({
        chapter,
        constructionTypeId,
        workOrderId,
        workOrderObjectId,
        useInPrices: false,
        checkedPriceRowForDetails: checkedPriceRowForDetails || totalChapterDetailsRowOpenOuter,
        priceLevel,
    });

    const detailsBaseRow = useMemo(
        () =>
            (checkedPriceRowForDetails || totalChapterDetailsRowOpenOuter)?.rowType ===
            BimWorkOrderRowTypeEnum.TotalsString
                ? []
                : [{ ...checkedPriceRowForDetails?.data, topPinnedRow: true }],
        [checkedPriceRowForDetails, totalChapterDetailsRowOpenOuter],
    );

    const detailsRows = useMemo(
        () =>
            (checkedPriceRowForDetails || totalChapterDetailsRowOpenOuter)?.rowType ===
            BimWorkOrderRowTypeEnum.TotalsString
                ? rowTotalsData
                : regularDetailsRows,
        [checkedPriceRowForDetails, regularDetailsRows, rowTotalsData, totalChapterDetailsRowOpenOuter],
    );

    const isDetailsLoading = useMemo(
        () =>
            (checkedPriceRowForDetails || totalChapterDetailsRowOpenOuter)?.rowType ===
            BimWorkOrderRowTypeEnum.TotalsString
                ? isTotalsLoading
                : isRegularDetailsLoading,
        [checkedPriceRowForDetails, isRegularDetailsLoading, isTotalsLoading, totalChapterDetailsRowOpenOuter],
    );

    return {
        detailsData: detailsRows ?? [],
        detailsBaseRow,
        isDetailsLoading,
    };
};
